import React, { useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { LEGAL, LEGAL_UPDATED, LEGAL_UI, COMPANY } from "@/lib/legal";
import { LANGUAGES } from "@/lib/languages";
import { ScrollText, ShieldCheck, Globe, FileText, Cookie, SlidersHorizontal } from "lucide-react";
import { OPEN_COOKIES_EVENT } from "@/components/CookieBanner";

const LEGAL_LANGS = ["fr", "en", "es", "de", "it", "pt"];

const DOC_ICON = { privacy: ShieldCheck, cookies: Cookie, mentions: FileText, terms: ScrollText };

export default function Legal({ doc }) {
  const { lang, setLang } = useLang();
  const code = LEGAL[lang] ? lang : "fr";
  const content = LEGAL[code][doc];
  const ui = LEGAL_UI[code] || LEGAL_UI.fr;
  const Icon = DOC_ICON[doc] || ScrollText;

  useEffect(() => {
    document.title = `${content.title} — Findloop`;
  }, [content.title]);

  return (
    <div className="bg-background min-h-[70vh]" data-testid={`legal-${doc}`}>
      <div className="relative bg-findo-navy text-white">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">
            <Icon size={14} /> findloop
          </span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold" data-testid="legal-title">{content.title}</h1>
          <p className="mt-3 text-white/80 max-w-xl">{content.intro}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
            <span className="text-white/60">{ui.updated} : {LEGAL_UPDATED}</span>
            <div className="relative">
              <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
              <select
                value={code}
                onChange={(e) => setLang(e.target.value)}
                className="appearance-none rounded-full bg-white/10 border border-white/25 py-2 pl-9 pr-8 text-sm font-semibold focus:border-white outline-none"
                data-testid="legal-lang-select">
                {LEGAL_LANGS.map((c) => {
                  const meta = LANGUAGES.find((l) => l.code === c);
                  return <option key={c} value={c} className="text-black">{meta.flag} {meta.name}</option>;
                })}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="space-y-8" data-testid="legal-sections">
          {content.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display text-xl md:text-lg font-semibold text-findo-navy">{s.h}</h2>
              <p className="mt-2 text-slate-600 leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
        {(doc === "cookies" || doc === "privacy") && (
          <button
            onClick={() => window.dispatchEvent(new Event(OPEN_COOKIES_EVENT))}
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-findo-navy text-white px-6 py-3 text-sm font-semibold hover:bg-findo-navy/90 transition-colors"
            data-testid="legal-manage-cookies">
            <SlidersHorizontal size={16} /> {ui.manageCookies}
          </button>
        )}
        <div className="mt-12 border-t border-border pt-6 text-xs text-slate-400 leading-relaxed" data-testid="legal-company-identity">
          {COMPANY.legalName}, {COMPANY.form} — {COMPANY.address} · SIREN {COMPANY.siren} · SIRET {COMPANY.siret} · {COMPANY.rcs} · TVA {COMPANY.vat} · Capital {COMPANY.capital} · {COMPANY.emailContact}
        </div>
      </div>
    </div>
  );
}
